import { useMemo } from 'react';

import { BoardingPass } from '@/components/BoardingPass';
import { calculatePriceDifference } from '@/utils/calculatePrice';

import type { FlightPlan } from '@/types/flights';
import type { Itinerary } from '@/types/itinerary';

interface FlightPlansProps {
  itinerary: Itinerary;
  flightPlan: FlightPlan;
  selected: boolean;
}

export function FlightPlans({ itinerary, flightPlan, selected }: FlightPlansProps) {
  const outboundFlight = useMemo(() => flightPlan.flights.find((flight) => flight.type === 'outward'), [flightPlan]);
  const returnFlight = useMemo(() => flightPlan.flights.find((flight) => flight.type === 'return'), [flightPlan]);
  const betweenFlights = useMemo(() => flightPlan.flights.filter((flight) => flight.type === 'between'), [flightPlan]);

  const parentFlightPlan = useMemo(() => {
    if (!selected) {
      return itinerary.flightPlans.find((fp) => fp.alternate === flightPlan);
    }
    return null;
  }, [itinerary.flightPlans, flightPlan, selected]);

  const priceDifference = useMemo(() => {
    if (parentFlightPlan) {
      return calculatePriceDifference(parentFlightPlan, flightPlan);
    }
    return null;
  }, [parentFlightPlan, flightPlan]);

  return (
    <>
      {!betweenFlights.length && (
        <BoardingPass
          itinerary={itinerary}
          outboundFlight={outboundFlight}
          returnFlight={returnFlight}
          betweenFlight={false}
          selected={selected}
          priceDifference={priceDifference ?? 0}
        />
      )}

      {betweenFlights.length > 0 &&
        betweenFlights.map((flight) => (
          <BoardingPass
            itinerary={itinerary}
            key={flight.id}
            outboundFlight={flight}
            betweenFlight
            selected={selected}
            priceDifference={priceDifference ?? 0}
          />
        ))}
    </>
  );
}
